import { get, isEmpty, compact, filter, includes, map, head } from 'lodash'
import React, { Component } from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ORG_NAME } from 'utils/env'
import cn from 'classnames'

import ItemsEdit from 'containers/ItemsEdit'
import DropdownSelect from 'components/DropdownSelect'
import Textbox from 'components/Textbox'
import { getDeviceWithConfiguration, getDevices } from 'store/Devices/selector'
import { deleteDevice } from 'store/Devices/actions'
import { getConfigurations, getSelectableConfigurations } from 'store/Configurations/selector'
import { formattedConfigurationLabel } from 'utils/devices'
import { getCurrentVenueId } from 'store/Venues/selector'
import ConfirmationModal from 'components/ConfirmationModal'

const SELECTABLE_TYPES = [
  {
    label: 'POS',
    value: 'pos',
  },
]

export class DevicesForm extends Component {
  static propTypes = {
    devices: PropTypes.array,
    venueId: PropTypes.string.isRequired,
    selectableConfigurations: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      }),
    ).isRequired,
    deleteDevice: PropTypes.func.isRequired,
    callApi: PropTypes.func.isRequired,
    editedItem: PropTypes.shape({
      deviceName: PropTypes.string,
      deviceId: PropTypes.string,
      configuration: PropTypes.object,
      appType: PropTypes.string,
      presence: PropTypes.object,
    }),
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
    callConfiguration: PropTypes.func,
    title: PropTypes.string,
  }

  constructor(props) {
    super(props)
    this.state = {
      isModalOpen: false,
      isSerialNumberTaken: false,
      form: {
        deviceId: get(props, 'editedItem.deviceId'),
        deviceName: get(props, 'editedItem.deviceName'),
        configurationUuid: get(props, 'editedItem.configuration.uuid') || '',
        appType: get(props, 'editedItem.appType') || SELECTABLE_TYPES[0].value,
      },
    }
  }

  resourceId = () => this.props.match.params.id

  onClose = () => {
    this.props.history.push(`/devices`)
  }

  handleChange = (event) => {
    const { name, value } = event.target
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        [name]: value,
      },
    }))
  }

  handleSubmit = (event) => {
    const { callApi, callConfiguration, venueId, editedItem, devices } = this.props
    const { form } = this.state
    event.preventDefault()

    const devicesIdsWithoutCurrentDeviceId = map(
      filter(devices, (device) => device.deviceId !== editedItem.deviceId),
      (device) => device.deviceId,
    )

    const payload = {
      ...form,
      appType: form.appType,
      venueUuid: venueId,
      organizationName: ORG_NAME,
    }

    const payloadOldConfiguration = {
      deviceId: null,
      uuid: editedItem.configuration.uuid,
      organizationName: ORG_NAME,
      venueUuid: venueId,
    }

    const payloadConfiguration = {
      deviceId: form.deviceId,
      uuid: form.configurationUuid,
      organizationName: ORG_NAME,
      venueUuid: venueId,
    }

    if (includes(devicesIdsWithoutCurrentDeviceId, form.deviceId)) {
      this.setState({ isSerialNumberTaken: true })
      return
    }

    callApi(payload).then(() => {
      if (isEmpty(editedItem.configuration)) {
        // check if there is no configuration linked ('Not set' option)
        callConfiguration({
          appDeviceConfigurations: [payloadConfiguration],
        }).then(() => {
          this.onClose()
        })
      } else if (form.configurationUuid === editedItem.configuration.uuid) {
        // check if selected option is the same as it was selected at the beginning
        // (user could for example select another option and then back to option which was selected at the beginning)
        // so no request is necessary
        this.onClose()
      } else if (form.configurationUuid === '') {
        // check if No Linked Configuration option has been set
        // it means that we unlink configuration from device
        callConfiguration({
          appDeviceConfigurations: [payloadOldConfiguration],
        })
        this.onClose()
      } else {
        // this request clear previous linked configuration
        callConfiguration({
          appDeviceConfigurations: [payloadOldConfiguration],
        }).then(() => {
          // this request set new linked configuration
          callConfiguration({
            appDeviceConfigurations: [payloadConfiguration],
          }).then(() => {
            this.onClose()
          })
        })
      }
    })
  }

  redirectToDevices = () => {
    this.props.history.push(`/devices`)
  }

  handleDeleteDevice = () => {
    const { deleteDevice, venueId } = this.props
    this.toggleModal()

    deleteDevice(venueId, this.resourceId()).then(() => {
      toast.success('Device successfully deleted')
      this.redirectToDevices()
    })
  }

  renderSelectedConfiguration = () => {
    const { editedItem } = this.props
    if (!isEmpty(editedItem.configuration)) {
      return {
        label: formattedConfigurationLabel(editedItem.configuration),
        value: editedItem.configuration.uuid,
      }
    } else {
      return null
    }
  }

  dropdownConfigurations = () => {
    const { selectableConfigurations, editedItem } = this.props

    const noLinkdedDeviceOption = !isEmpty(get(editedItem, 'configuration.uuid'))
      ? { label: 'No linked configuration', value: '' }
      : null

    const linkedConfigurationOptions = compact([...selectableConfigurations, noLinkdedDeviceOption])

    if (this.resourceId()) {
      return compact([this.renderSelectedConfiguration(), ...linkedConfigurationOptions])
    } else {
      return linkedConfigurationOptions
    }
  }

  toggleModal = () =>
    this.setState((prevState) => ({
      isModalOpen: !prevState.isModalOpen,
    }))

  render() {
    const {
      isModalOpen,
      isSerialNumberTaken,
      form: { deviceId, deviceName, appType, configurationUuid },
    } = this.state
    const { editedItem, title } = this.props

    let presence = 'Disconnected'

    if (editedItem.presence) {
      presence = head(Object.values(editedItem.presence)) ? 'Connected' : 'Disconnected'
    }

    return (
      <ItemsEdit onClose={this.onClose} title={title}>
        <form onSubmit={this.handleSubmit}>
          <section className="form__fieldset">
            <Textbox
              errorMsg="This serial number is already taken"
              isError={isSerialNumberTaken}
              label="Device (Serial Number)"
              min={0}
              name="deviceId"
              onChange={this.handleChange}
              onFocus={() => this.setState({ isSerialNumberTaken: false })}
              readOnly={!isEmpty(this.resourceId())}
              required
              value={deviceId}
            />
            <Textbox
              label="Device Name"
              name="deviceName"
              onChange={this.handleChange}
              value={deviceName}
            />
            <DropdownSelect
              className="-block"
              fullWidth
              isEmptyOption={isEmpty(editedItem.configuration)}
              isValid
              label="Linked Configuration"
              name="configurationUuid"
              onChange={this.handleChange}
              options={this.dropdownConfigurations()}
              value={configurationUuid}
            />
            <DropdownSelect
              className="-block"
              fullWidth
              isValid
              label="Type"
              name="appType"
              onChange={this.handleChange}
              options={SELECTABLE_TYPES}
              required
              value={appType}
            />
            <div className="form__status margin-top-base">
              <span>Connection Status</span>
              <div className="d-flex align-items-center">
                <span
                  className={cn('form__dot', {
                    '-on': presence.toString() === 'Connected',
                    '-off': presence.toString() === 'Disconnected',
                  })}
                ></span>
                <span>{presence.toString() === 'Connected' ? 'Connected' : 'Disconnected'}</span>
              </div>
            </div>
          </section>
          <section className="form__buttons">
            <button className="btn -lg -primary -block margin-bottom-base" type="submit">
              Save
            </button>
            {this.resourceId() && isEmpty(get(editedItem, 'configuration.uuid')) && (
              <button className="btn -lg -outline -block" onClick={this.toggleModal} type="button">
                Delete device
              </button>
            )}
          </section>
        </form>
        <ConfirmationModal
          isOpen={isModalOpen}
          onConfirm={this.handleDeleteDevice}
          toggleModal={this.toggleModal}
        />
      </ItemsEdit>
    )
  }
}

const mapStateToProps = (state, ownProps) => ({
  devices: getDevices(state),
  editedItem: getDeviceWithConfiguration(state, get(ownProps, ['match', 'params', 'id'])),
  configurations: getConfigurations(state),
  selectableConfigurations: getSelectableConfigurations(state),
  venueId: getCurrentVenueId(state),
})

const mapDispatchToProps = {
  deleteDevice,
}

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(DevicesForm)
